import { ErrorBoundary } from "@sentry/react";
import { useCallback, useMemo, useState } from "react";
import { Container, Dropdown, Form } from "react-bootstrap";

import { DataTable } from "@/components/DataTable";
import { PageLoader } from "@/components/PageLoader";
import { TableWrapper } from "@/components/TableWrapper";
import { useTrackedQuery } from "@/hooks/useTrackedQuery";
import { SosCustomFields } from "@/utils/sos/custom-fields";
import { vendorAssessmentLogQueryOptions } from "./vendor-assessment-log-query";
import {
  vendorAssessmentLogColumns,
  VendorAssessmentLogTableRow,
  VendorAssessmentStatus,
} from "./vendor-assessment-log-table-columns";
import { Icon } from "@/components/Icon";

const allAssessmentStatuses: VendorAssessmentStatus[] = [
  "Not Needed",
  "Not Started",
  "In-Progress",
  "Complete - Approved",
  "Complete - Rejected",
  "Unknown",
];

export const VendorAssessmentLog: React.FC = () => {
  // Define State
  const [showArchived, setShowArchived] = useState(false);
  const [showStatuses, setShowStatuses] = useState(
    new Set<VendorAssessmentStatus>([
      "Not Started",
      "In-Progress",
      "Complete - Approved",
      "Complete - Rejected",
      "Unknown",
    ]),
  );

  // Define callbacks
  const toggleShowArchived = useCallback(() => {
    setShowArchived((x) => !x);
  }, []);
  const toggleShowStatus = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const status = e.target.value as VendorAssessmentStatus;
    setShowStatuses((prev) => {
      const newStatuses = new Set(prev);
      if (newStatuses.has(status)) newStatuses.delete(status);
      else newStatuses.add(status);
      return newStatuses;
    });
  }, []);

  // Fetch data
  const vendorsQuery = useTrackedQuery(vendorAssessmentLogQueryOptions);

  // Determine the assessment status for all vendors before filtering
  const allVendors = useMemo(() => {
    return (vendorsQuery.data ?? []).map((vendor) => {
      const assessmentLink = SosCustomFields.getStringValue(vendor, "assessmentLink") || "";

      let assessmentStatus = SosCustomFields.getListValue(vendor, "assessmentStatus") as VendorAssessmentStatus | null;
      if (assessmentStatus !== "Not Needed" && !assessmentLink) assessmentStatus = "Not Started";
      assessmentStatus ||= "Unknown";

      return { ...vendor, assessmentLink, assessmentStatus };
    });
  }, [vendorsQuery.data]);

  // Filter and map query data into table format
  // NOTE: We aren't using the 'select' option in the query because we want to show the user the number of unfiltered records
  // Note: Use the useMemo hook here so that we aren't recomputing the table data on every render
  const tableData: VendorAssessmentLogTableRow[] = useMemo(() => {
    return allVendors
      .filter((x) => showArchived || !x.archived)
      .filter((x) => showStatuses.has(x.assessmentStatus))
      .map((vendor) => ({
        ...vendor,
        currencyFlag:
          vendor.currency?.code === "CAD" ? "🇨🇦"
          : vendor.currency?.code === "USD" ? "🇺🇸"
          : undefined,
      }));
  }, [allVendors, showArchived, showStatuses]);

  // Calculate some numbers for displaying on the filters
  const numArchived = allVendors.filter((x) => x.archived).length;

  // Handle loading and error states
  // NOTE: This must be done after all hooks have executed; hooks must be called in the same order on every render
  if (vendorsQuery.isError) return `Error loading sales orders: ${vendorsQuery.error.message}`;
  if (vendorsQuery.isPending) return <PageLoader text="Loading Vendor Assessments" />;

  return (
    <ErrorBoundary>
      <Container fluid className="py-3 h-100 d-flex flex-column">
        <div className="col-auto">
          <h2>
            Vendor Assessments{" "}
            <small className="text-body-secondary">
              (Displaying {tableData.length} of {vendorsQuery.data.length})
            </small>
            <a
              href="https://drive.google.com/drive/folders/1M2tqcE3y2_G1hIxS1FyCYPB6uNSybt6j"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-secondary btn-sm ms-3 align-middle d-inline-flex align-items-center"
            >
              Google Drive Folder
              <Icon icon="box-arrow-up-right" className="ms-1" />
            </a>
          </h2>
          <div className="mb-3">
            <div className="row row-cols-auto g-4 align-items-center">
              <span>Filters:</span>
              <Form.Check
                type="switch"
                label={`Show Archived (${numArchived.toFixed()})`}
                checked={showArchived}
                onChange={toggleShowArchived}
              />
              <Dropdown autoClose="outside">
                <Dropdown.Toggle variant="secondary" size="sm">
                  Assessment Status ({showStatuses.size}/{allAssessmentStatuses.length} selected)
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {allAssessmentStatuses.map((status) => (
                    <Dropdown.ItemText key={status}>
                      <Form.Check
                        type="checkbox"
                        id={`assessment-status-${status}`}
                        label={status}
                        value={status}
                        checked={showStatuses.has(status)}
                        onChange={toggleShowStatus}
                      />
                    </Dropdown.ItemText>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>
        <TableWrapper className="col">
          <DataTable
            bordered
            hover
            columns={vendorAssessmentLogColumns}
            data={tableData}
            initialState={{
              sorting: [{ id: "name", desc: false }],
            }}
            state={{
              columnVisibility: {
                // Hide the 'archived' column when archived records are filtered out
                archived: showArchived,
              },
            }}
          />
        </TableWrapper>
      </Container>
    </ErrorBoundary>
  );
};
